import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { Box, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import ThemeProvider from './components/main/ThemeProvider';
import I18nProvider from './components/main/I18nProvider';
import { AuthProvider } from './auth';
import { WishlistProvider } from './contexts/WishlistContext';
import { BrandProvider } from './contexts/BrandContext';
import { ToastProvider } from './contexts/ToastContext';
import Header from './components/main/Header';
import Footer from './components/main/Footer';
import MobileBottomNav from './components/main/MobileBottomNav';
import AppRouter from './routers';
import { CartManagerProvider } from './cart/CartManager';
import { initializeFirebase } from './config/firebase';
import WhatsAppButton from './components/main/WhatsAppButton';
import { useConfig } from './api/config';

// Component to conditionally render header based on route
// This component hides the main app header on mobile devices for specific pages
// where the page has its own header (like back button + title) to save screen space
function ConditionalHeader() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Pages where header should be hidden on mobile
  // These pages have their own page headers with back buttons and titles
  const mobileHiddenPages = [
    '/brands',           // All brands listing
    '/brand/',           // Individual brand details pages
    '/new-products',     // New products listing
    '/sale-products',    // Sale products listing
    '/our-picks',        // Our picks details
    '/categories/',      // Category product listings
    '/search'            // Search results page
  ];
  
  // Check if current page should hide header on mobile
  const shouldHideHeader = isMobile && mobileHiddenPages.some(page => 
    location.pathname.startsWith(page)
  );
  
  // Debug logging for development
  console.log('Header visibility check:', {
    pathname: location.pathname,
    isMobile,
    shouldHideHeader,
    matchingPages: mobileHiddenPages.filter(page => location.pathname.startsWith(page)),
    userAgent: navigator.userAgent
  });
  
  if (shouldHideHeader) {
    console.log('Hiding header on mobile for:', location.pathname);
    return null;
  }
  
  console.log('Showing header for:', location.pathname);
  return <Header />;
}

// Component to render WhatsApp button based on config
function WhatsAppWrapper() {
  const { config } = useConfig();
  
  if (!config) {
    return null;
  }
  
  return (
    <WhatsAppButton 
      whatsAppLink={config.whatsAppLink} 
      enabled={config.whatsAppEnabled} 
    />
  );
}

function App() {
  const [firebaseReady, setFirebaseReady] = useState(false);

  // Initialize Firebase when the app starts
  useEffect(() => {
    const initFirebase = async () => {
      try {
        await initializeFirebase();
        setFirebaseReady(true);
      } catch (error) {
        // Even if Firebase fails, we should still render the app
        setFirebaseReady(true);
        console.log(error);
      }
    };

    initFirebase();
  }, []);

  return (
    <SWRConfig
      value={{
        fetcher: (url: string) => fetch(url).then(res => res.json()),
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        errorRetryCount: 3,
      }}
    >
      <Router>
        <div
          style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
          <I18nProvider>
            <ThemeProvider>
              {firebaseReady ? (
                <>
                  {console.log('🚀 App: Rendering AuthProvider and app components')}
                  <AuthProvider>
                    <WishlistProvider>
                      <BrandProvider>
                        <ToastProvider>
                          <CartManagerProvider>
                            <ConditionalHeader />
                            <Box
                              component="main"
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                pb: { xs: '60px', md: 0 }
                              }}
                            >
                              <AppRouter />
                            </Box>
                            <Footer />
                            <MobileBottomNav />
                            <WhatsAppWrapper />
                          </CartManagerProvider>
                        </ToastProvider>
                      </BrandProvider>
                    </WishlistProvider>
                  </AuthProvider>
                </>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100vh',
                  gap: '16px'
                }}>
                  <CircularProgress size={40} />
                </div>
              )}
            </ThemeProvider>
          </I18nProvider>
        </div>
      </Router>
    </SWRConfig>
  );
}

export default App;
